import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { corsReadUrl } from '../canvas/images';
import { brandKit, fileContext, setFileBrand, type BrandKit, type FileContext } from '../services/work';

/**
 * What the open file is part of — its project, and the brand it is made in — for the
 * chrome. The Assets panel shows the project's things; the colour picker, the Text panel
 * and the Brand panel wear the brand.
 *
 * Read when the file opens, and again when Assets is opened (a brand is changed in
 * another app, and nothing tells this one). Everything here is an addition: while it is
 * loading, or when it cannot be read, the editor is the editor it is for a file in no
 * project.
 */
export interface Work {
  /** Null until it has been read, or when it could not be. */
  readonly context: FileContext | null;
  /** The brand in force, as the editor uses it. Null with no brand, and while it loads. */
  readonly kit: BrandKit | null;
  /** Makes this the file's brand: one by id, none (null), or the project's again ('project'). */
  choose(brandId: string | null | 'project'): Promise<void>;
  /** Reads it all again. */
  refresh(): void;
}

const NOTHING: Work = { context: null, kit: null, choose: async () => {}, refresh: () => {} };

export const WorkContext = createContext<Work>(NOTHING);

export function useWork(): Work {
  return useContext(WorkContext);
}

/** The brand in force, for a control that only wants its colours or its type. */
export function useKit(): BrandKit | null {
  return useContext(WorkContext).kit;
}

/** Kits already read, by brand: switching back to a brand is at once, and asked again behind it. */
const kits = new Map<string, BrandKit>();

export function useWorkOf(documentId: string, ready: boolean, onNotice: (message: string) => void): Work {
  const [context, setContext] = useState<FileContext | null>(null);
  const [kit, setKit] = useState<BrandKit | null>(null);
  const [again, setAgain] = useState(0);
  /** Which file the answer on screen belongs to: a slow answer for the last file is dropped. */
  const asked = useRef(0);

  useEffect(() => {
    if (!ready) return;
    const mine = ++asked.current;
    fileContext(documentId)
      .then((found) => {
        if (asked.current === mine) setContext(found);
      })
      .catch(() => {
        // Left as it was: a file whose project could not be asked about is still a file.
      });
  }, [documentId, ready, again]);

  const brandId = context?.brand?.id ?? null;
  useEffect(() => {
    if (!brandId) {
      setKit(null);
      return;
    }
    let live = true;
    setKit(kits.get(brandId) ?? null);
    brandKit(brandId)
      .then((found) => {
        kits.set(brandId, found);
        if (live) setKit(found);
      })
      .catch(() => {
        if (live && !kits.has(brandId)) setKit(null);
      });
    return () => {
      live = false;
    };
  }, [brandId, again]);

  const choose = useCallback(
    async (next: string | null | 'project') => {
      try {
        setContext(await setFileBrand(documentId, next));
      } catch (error) {
        onNotice(error instanceof Error ? error.message : 'The brand could not be changed.');
      }
    },
    [documentId, onNotice],
  );
  const refresh = useCallback(() => setAgain((n) => n + 1), []);

  return { context, kit, choose, refresh };
}

/** Faces already handed to the browser, by family, weight and slant: asked for once a visit. */
const loaded = new Set<string>();

/**
 * Lets the CHROME draw in the brand's faces — a Text preset shown in the face it will be
 * set in. The canvas loads a file's fonts itself; this is only so the panel does not
 * show a serif brand's heading in the system's sans. A face that will not load is left:
 * the preset is then drawn in the panel's own face and still places the brand's.
 */
export function useKitFonts(kit: BrandKit | null): void {
  useEffect(() => {
    if (!kit || typeof FontFace === 'undefined') return;
    for (const font of kit.fonts) {
      for (const face of font.faces) {
        const key = `${font.family}|${face.weight}|${face.italic}`;
        if (loaded.has(key)) continue;
        loaded.add(key);
        const made = new FontFace(font.family, `url("${corsReadUrl(face.url)}")`, { weight: String(face.weight), style: face.italic ? 'italic' : 'normal' });
        made.load().then(
          (ready) => document.fonts.add(ready),
          () => loaded.delete(key),
        );
      }
    }
  }, [kit]);
}
